import { axiosPrivate } from '../axiosClient';
import type { AppNotification } from '../redux/notificationsSlice';

// The auth interceptor adds the token and the current location header, so
// every call here is scoped to the logged-in user at the current location.

export type ServerNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, any> | null;
  locationId: string;
  createdAt: number;
  isRead: boolean;
};

export type NotificationFilter = 'all' | 'unread' | 'read';

export type NotificationsPage = {
  docs: AppNotification[];
  page: number;
  hasNextPage: boolean;
  totalDocs: number;
};

export const toAppNotification = (
  row: ServerNotification,
): AppNotification => ({
  id: String(row.id),
  title: String(row.title || '').trim() || 'Notification',
  body: String(row.body || '').trim(),
  createdAt: Number(row.createdAt) || Date.now(),
  isRead: Boolean(row.isRead),
  source: 'server',
  data: row.data || undefined,
  locationId: String(row.locationId || ''),
  type: row.type,
});

const readSuccess = (response: any, fallback: string) => {
  const payload = response?.data;
  if (!payload?.success) {
    throw new Error(payload?.message || fallback);
  }
  return payload.data;
};

export async function fetchNotifications(
  params: { page?: number; limit?: number; filter?: NotificationFilter } = {},
): Promise<NotificationsPage> {
  const response = await axiosPrivate.get('/bp_get_notifications_information', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 50,
      filter: params.filter ?? 'all',
    },
  });
  const data = readSuccess(response, 'Failed to load notifications');
  return {
    docs: (Array.isArray(data?.docs) ? data.docs : []).map(toAppNotification),
    page: Number(data?.page) || 1,
    hasNextPage: Boolean(data?.hasNextPage),
    totalDocs: Number(data?.totalDocs) || 0,
  };
}

export async function setNotificationReadOnServer(
  id: string,
  isRead: boolean,
): Promise<void> {
  await axiosPrivate.patch(
    `/bp_update_notification_read_information/${encodeURIComponent(id)}`,
    { isRead },
  );
}

/** Current location only. */
export async function markAllNotificationsReadOnServer(): Promise<void> {
  await axiosPrivate.patch('/bp_mark_all_notifications_read_information');
}

export async function deleteNotificationOnServer(id: string): Promise<void> {
  await axiosPrivate.delete(
    `/bp_delete_notification_information/${encodeURIComponent(id)}`,
  );
}

/** Current location only. */
export async function clearNotificationsOnServer(
  scope: 'read' | 'all',
): Promise<void> {
  await axiosPrivate.delete('/bp_clear_notifications_information', {
    params: { scope },
  });
}
